import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";
import {
  Table, Modal, Form, Input, InputNumber, Select, Switch, message,
  Popconfirm, Button, Space,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  getAssessTypeList, createAssessType, updateAssessType, deleteAssessType, toggleAssessTypeUse,
  getAssessTypeGroupList,
} from "../api/domainApi";
import { AssessTypeRes } from "../types/domain";
import PageHeader from "../components/common/PageHeader";
import UseYnTag from "../components/common/UseYnTag";

const PAGE_SIZE = 20;

const coordOptions = [
  { value: undefined, label: "전체" },
  { value: 1, label: "1" },
  { value: 2, label: "2" },
  { value: 3, label: "3" },
];

const AssessTypePage: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState("");
  const [kwInput, setKwInput] = useState("");
  const [filterGrpId, setFilterGrpId] = useState<number | undefined>();
  const [filterCoordX, setFilterCoordX] = useState<number | undefined>();
  const [filterCoordY, setFilterCoordY] = useState<number | undefined>();
  const [filterCoordZ, setFilterCoordZ] = useState<number | undefined>();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<AssessTypeRes | null>(null);
  const [form] = Form.useForm();

  const swrKey = `/assess-type?page=${page}&kw=${keyword}&grp=${filterGrpId}&x=${filterCoordX}&y=${filterCoordY}&z=${filterCoordZ}`;
  const { data, isLoading, mutate } = useSWR(
    swrKey,
    () => getAssessTypeList({
      page: page - 1, size: PAGE_SIZE, keyword,
      typeGrpId: filterGrpId,
      coordX: filterCoordX,
      coordY: filterCoordY,
      coordZ: filterCoordZ,
    }),
    { keepPreviousData: true }
  );

  const { data: groupsData } = useSWR("/assess-type-group", () => getAssessTypeGroupList({ size: 100 }));
  const groups = groupsData?.content;

  const openCreate = () => { setEditing(null); form.resetFields(); setModalOpen(true); };
  const openEdit = (row: AssessTypeRes) => { setEditing(row); form.setFieldsValue(row); setModalOpen(true); };

  const handleOk = async () => {
    const values = await form.validateFields();
    try {
      if (editing) {
        await updateAssessType(editing.id, values);
        message.success("수정되었습니다.");
      } else {
        await createAssessType(values);
        message.success("추가되었습니다.");
      }
      setModalOpen(false);
      mutate();
    } catch (e: any) {
      message.error(e.message);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteAssessType(id);
      message.success("삭제되었습니다.");
      mutate();
    } catch (e: any) {
      message.error(e.message);
    }
  };

  const columns: ColumnsType<AssessTypeRes> = [
    { title: "ID", dataIndex: "id", width: 70 },
    { title: "그룹", dataIndex: "typeGrpName", width: 100 },
    { title: "코드", dataIndex: "typeCode", width: 90 },
    { title: "유형명", dataIndex: "typeName", ellipsis: { showTitle: true } },
    { title: "수식어", dataIndex: "subName", width: 120 },
    { title: "좌표", width: 90, render: (_, r) => `(${r.coordX},${r.coordY},${r.coordZ})` },
    { title: "기본", dataIndex: "isDefault", width: 60, render: (v) => v ? "O" : "-" },
    { title: "사용여부", dataIndex: "useYn", width: 90, render: (v) => <UseYnTag value={v} /> },
    {
      title: "관리",
      width: 220,
      render: (_, row) => (
        <Space>
          <Button size="small" onClick={() => openEdit(row)}>수정</Button>
          <Button size="small" type="dashed" onClick={() => navigate(`/assess-types/${row.id}`)}>상세</Button>
          <Button size="small" onClick={async () => {
            try { await toggleAssessTypeUse(row.id); mutate(); }
            catch (e: any) { message.error(e.message); }
          }}>
            {row.useYn ? "비활성" : "활성"}
          </Button>
          <Popconfirm title="삭제하시겠습니까?" onConfirm={() => handleDelete(row.id)}>
            <Button size="small" danger>삭제</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const resetFilters = () => {
    setKwInput(""); setKeyword("");
    setFilterGrpId(undefined);
    setFilterCoordX(undefined);
    setFilterCoordY(undefined);
    setFilterCoordZ(undefined);
    setPage(1);
  };

  return (
    <div>
      <PageHeader title="성향유형" onAdd={openCreate} />
      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          placeholder="그룹 선택" allowClear style={{ width: 180 }}
          options={groups?.map((g) => ({ value: g.id, label: g.grpName }))}
          onChange={(v) => { setFilterGrpId(v); setPage(1); }}
          value={filterGrpId}
        />
        <Select
          placeholder="X좌표" allowClear style={{ width: 100 }}
          options={coordOptions.filter((o) => o.value !== undefined)}
          onChange={(v) => { setFilterCoordX(v); setPage(1); }}
          value={filterCoordX}
        />
        <Select
          placeholder="Y좌표" allowClear style={{ width: 100 }}
          options={coordOptions.filter((o) => o.value !== undefined)}
          onChange={(v) => { setFilterCoordY(v); setPage(1); }}
          value={filterCoordY}
        />
        <Select
          placeholder="Z좌표" allowClear style={{ width: 100 }}
          options={coordOptions.filter((o) => o.value !== undefined)}
          onChange={(v) => { setFilterCoordZ(v); setPage(1); }}
          value={filterCoordZ}
        />
        <Input placeholder="유형명/코드 검색" value={kwInput}
          onChange={(e) => setKwInput(e.target.value)}
          onPressEnter={() => { setKeyword(kwInput); setPage(1); }}
          style={{ width: 220 }}
        />
        <Button onClick={() => { setKeyword(kwInput); setPage(1); }}>검색</Button>
        <Button onClick={resetFilters}>초기화</Button>
      </Space>
      <Table
        rowKey="id" columns={columns} dataSource={data?.content} loading={isLoading}
        pagination={{ current: page, pageSize: PAGE_SIZE, total: data?.page?.totalElements, onChange: setPage, showSizeChanger: false }}
        size="small"
      />

      <Modal
        title={editing ? "성향유형 수정" : "성향유형 추가"}
        open={modalOpen} onOk={handleOk} onCancel={() => setModalOpen(false)}
        okText={editing ? "수정" : "추가"} cancelText="취소"
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="typeCode" label="유형 코드" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="typeName" label="유형명" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="subName" label="수식어" rules={[{ required: true }]}><Input /></Form.Item>
          <Space>
            <Form.Item name="coordX" label="X좌표 (1~3)" rules={[{ required: true }]}>
              <InputNumber min={1} max={3} />
            </Form.Item>
            <Form.Item name="coordY" label="Y좌표 (1~3)" rules={[{ required: true }]}>
              <InputNumber min={1} max={3} />
            </Form.Item>
            <Form.Item name="coordZ" label="Z좌표 (1~3)" rules={[{ required: true }]}>
              <InputNumber min={1} max={3} />
            </Form.Item>
          </Space>
          <Form.Item name="isDefault" label="기본 타이틀" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AssessTypePage;
